import AddIcon from '@mui/icons-material/Add';
import { Button, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { IRowNode } from 'ag-grid-community';
import { useSetAtom } from 'jotai';
import { ReactNode, useCallback, useState } from 'react';
import { SearchInput } from '../../../CoreComponents/base/SearchInput';
import { CompanyType } from '../../../data-models/company.data-model';
import { ReservesAction, showReservesFormState } from '../state/FPState';
import { getFundReservesViewPrefs, setFundReservesViewPrefs } from './fundReservesViewPrefs';
import { isReservesDraftRow, ReservesRowData } from './reservesGridData';

export const InvestmentFilterOption = {
  future: 'Future Investments',
  current: 'Current Investments',
} as const;

export type InvestmentFilterOption = keyof typeof InvestmentFilterOption;

interface IReservesGridFiltersProps {
  onSearch: (value: string) => void;
  investmentFilter?: InvestmentFilterOption | null;
  onInvestmentFilterChange: (value: InvestmentFilterOption) => void;
  actionsComponent?: ReactNode;
}
export function ReservesGridFiltersAndActions({
  actionsComponent,
  onSearch,
  investmentFilter,
  onInvestmentFilterChange,
}: IReservesGridFiltersProps) {
  return (
    <Stack
      display={'grid'}
      gridTemplateColumns={'1fr auto auto'}
      justifyContent={'space-between'}
      alignItems={'center'}
      mb='0.5rem'
    >
      <SearchInput
        style={{ maxWidth: '22rem' }}
        onChange={onSearch}
        placeholder='Search by company name or website'
      />
      <Stack direction={'row'} gap={'0.5rem'} alignItems={'center'}>
        <InvestmentTypeFilter
          investmentFilter={investmentFilter}
          onInvestmentFilterChange={onInvestmentFilterChange}
        />
        <AddInvestmentButton />
        {actionsComponent}
      </Stack>
    </Stack>
  );
}

interface IInvestmentTypeFilterProps {
  investmentFilter?: InvestmentFilterOption | null;
  onInvestmentFilterChange: (value: InvestmentFilterOption) => void;
}
export function InvestmentTypeFilter({
  investmentFilter,
  onInvestmentFilterChange,
}: IInvestmentTypeFilterProps) {
  return (
    <ToggleButtonGroup
      color='secondary'
      value={investmentFilter}
      exclusive
      onChange={(e, newValue: InvestmentFilterOption) => {
        onInvestmentFilterChange(newValue);
      }}
      aria-label='Select investment'
    >
      <ToggleButton value={'future'}>{InvestmentFilterOption.future}</ToggleButton>
      <ToggleButton value={'current'}>{InvestmentFilterOption.current}</ToggleButton>
    </ToggleButtonGroup>
  );
}

export function useReservesExternalFilters(enableSaveView: boolean) {
  const [investmentFilter, setInvestmentFilter] = useState<InvestmentFilterOption | null>(() =>
    enableSaveView ? (getFundReservesViewPrefs()?.investmentTypeFilter ?? null) : null
  );

  const onInvestmentFilterChange = useCallback(
    (value: InvestmentFilterOption) => {
      setInvestmentFilter(value);
      if (enableSaveView) {
        setFundReservesViewPrefs({ investmentTypeFilter: value });
      }
    },
    [enableSaveView]
  );

  const doesExternalFilterPass = useCallback(
    (node: IRowNode<ReservesRowData>) => {
      // the in-progress draft must stay visible in either filter mode
      if (isReservesDraftRow(node.data)) {
        return true;
      }
      if (investmentFilter === 'future') {
        return node.data?.company?.companyType !== CompanyType.portfolio;
      } else if (investmentFilter === 'current') {
        return node.data?.company?.companyType === CompanyType.portfolio;
      } else {
        return true;
      }
    },
    [investmentFilter]
  );
  const isExternalFilterPresent = useCallback(() => investmentFilter !== null, [investmentFilter]);

  return { investmentFilter, onInvestmentFilterChange, doesExternalFilterPass, isExternalFilterPresent };
}

export function AddInvestmentButton() {
  const setShowReservesForm = useSetAtom(showReservesFormState);
  return (
    <Button
      variant='contained'
      color='secondary'
      size='medium'
      startIcon={<AddIcon />}
      onClick={() => setShowReservesForm(ReservesAction.AddInvestment)}
    >
      Add
    </Button>
  );
}
